import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import NoticeboardSkeletonLoader from '../../skeletons/NoticeboardSkeletonLoader';
import CustomPaginator from '../../components/CustomPaginator';
import GlobalHeaderTitle from '../../components/GlobalHeaderTitle';
import ViewNoticeModal from './parts/ViewNoticeModal';
import AddNoticeModal from './parts/AddNoticeModal';
import GlobalDeleteModal from '../../components/GlobalDeleteModal';

interface NoticeData {
  _id: string;
  title: string;
  body: string;
  files: Array<{ file_name: string; cdn_url: string; type: string }>;
  posted_by_name: string;
  date_time: string;
  priority: string;
  created_at: string;
  updated_at: string;
}

interface PaginationData {
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

const Noticeboard: React.FC = () => {
  const [notices, setNotices] = useState<NoticeData[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [paginationData, setPaginationData] = useState<PaginationData>({
    total: 0, page: 1, limit: 10, total_pages: 1,
  });

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedNoticeId, setSelectedNoticeId] = useState<string | null>(null);

  const [addEditModalOpen, setAddEditModalOpen] = useState(false);
  const [editingNoticeId, setEditingNoticeId] = useState<string | null>(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingNoticeId, setDeletingNoticeId] = useState<string | null>(null);
  const [deletingNotice, setDeletingNotice] = useState<NoticeData | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const toast = useRef<Toast>(null);

  useEffect(() => {
    fetchNotices();
  }, [page]);

  const fetchNotices = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/api/noticeboard?page=${page}&limit=10`);
      if (response.data.status === 'success') {
        await new Promise(resolve => setTimeout(resolve, 800));
        setNotices(response.data.notices || []);
        setPaginationData(response.data.pagination || { total: 0, page: 1, limit: 10, total_pages: 1 });
      } else {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: response.data.message || 'Failed to fetch notices', life: 4000 });
      }
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.message || 'Failed to fetch notices', life: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const handleAddNotice = () => {
    setEditingNoticeId(null);
    setAddEditModalOpen(true);
  };

  const handleEditNotice = (noticeId: string) => {
    setEditingNoticeId(noticeId);
    setAddEditModalOpen(true);
  };

  const handleSuccess = (shouldResetPage: boolean = false) => {
    if (shouldResetPage) setPage(1);
    fetchNotices();
    toast.current?.show({
      severity: 'success',
      summary: 'Success',
      detail: editingNoticeId ? 'Notice updated successfully!' : 'Notice added successfully!',
      life: 4000
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deletingNoticeId) return;
    setDeleteLoading(true);
    try {
      await apiClient.delete(`/api/noticeboard/${deletingNoticeId}`);
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Notice deleted successfully!', life: 4000 });
      setDeleteModalOpen(false);
      setDeletingNoticeId(null);
      setDeletingNotice(null);
      if (notices.length === 1 && page > 1) {
        setPage(prev => prev - 1);
      } else {
        fetchNotices();
      }
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.message || 'Failed to delete notice', life: 5000 });
    } finally {
      setDeleteLoading(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high':   return { bg: 'linear-gradient(135deg, rgba(239,68,68,0.1) 0%, rgba(220,38,38,0.08) 100%)',   border: 'rgba(239,68,68,0.2)',   text: '#dc2626', dot: '#ef4444' };
      case 'medium': return { bg: 'linear-gradient(135deg, rgba(245,158,11,0.1) 0%, rgba(217,119,7,0.08) 100%)',  border: 'rgba(245,158,11,0.2)',  text: '#d97706', dot: '#f59e0b' };
      case 'low':    return { bg: 'linear-gradient(135deg, rgba(34,197,94,0.1) 0%, rgba(22,163,74,0.08) 100%)',   border: 'rgba(34,197,94,0.2)',   text: '#16a34a', dot: '#22c55e' };
      default:       return { bg: 'linear-gradient(135deg, rgba(107,114,128,0.1) 0%, rgba(75,85,99,0.08) 100%)',  border: 'rgba(107,114,128,0.2)', text: '#4b5563', dot: '#6b7280' };
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    };
  };

  const serialNumberTemplate = (_data: NoticeData, options: ColumnBodyOptions) => {
    const index = options.rowIndex + ((page - 1) * paginationData.limit);
    return (
      <div style={{
        width: '28px', height: '28px', background: '#f9fafb', borderRadius: '6px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 700, fontSize: '12px', color: '#6b7280', margin: '0 auto'
      }}>
        {index + 1}
      </div>
    );
  };

  const titleTemplate = (rowData: NoticeData) => (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontWeight: 600, fontSize: '13px', color: '#1f2937', marginBottom: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {rowData.title}
      </div>
      <div style={{ fontSize: '12px', color: '#9ca3af', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '300px' }}>
        {rowData.body}
      </div>
    </div>
  );

  const priorityTemplate = (rowData: NoticeData) => {
    const c = getPriorityColor(rowData.priority);
    return (
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '20px', background: c.bg, border: `1px solid ${c.border}` }}>
        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: c.dot, boxShadow: `0 0 6px ${c.dot}` }} />
        <span style={{ fontSize: '11px', color: c.text, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {rowData.priority}
        </span>
      </div>
    );
  };

  const postedByTemplate = (rowData: NoticeData) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <div style={{
        width: '32px', height: '32px', borderRadius: '50%',
        background: 'linear-gradient(135deg, #4169E1 0%, #5B7CE6 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '12px', fontWeight: 600, color: 'white', flexShrink: 0
      }}>
        {rowData.posted_by_name?.charAt(0).toUpperCase() || 'U'}
      </div>
      <div style={{ fontSize: '13px', color: '#1f2937', fontWeight: 500 }}>
        {rowData.posted_by_name || 'Unknown'}
      </div>
    </div>
  );

  const dateTimeTemplate = (rowData: NoticeData) => {
    const dt = formatDateTime(rowData.date_time);
    return (
      <div>
        <div style={{ fontSize: '13px', color: '#1f2937', fontWeight: 500, marginBottom: '2px' }}>{dt.date}</div>
        <div style={{ fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace' }}>{dt.time}</div>
      </div>
    );
  };

  const actionTemplate = (rowData: NoticeData) => (
    <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
      <Button
        icon="pi pi-eye"
        className="p-button-text p-button-rounded"
        tooltip="View" tooltipOptions={{ position: 'top' }}
        style={{ color: '#60a5fa', width: '32px', height: '32px' }}
        onClick={() => { setSelectedNoticeId(rowData._id); setViewModalOpen(true); }}
      />
      <Button
        icon="pi pi-pencil"
        className="p-button-text p-button-rounded"
        tooltip="Edit" tooltipOptions={{ position: 'top' }}
        style={{ color: '#3b82f6', width: '32px', height: '32px' }}
        onClick={() => handleEditNotice(rowData._id)}
      />
      <Button
        icon="pi pi-trash"
        className="p-button-text p-button-rounded"
        tooltip="Delete" tooltipOptions={{ position: 'top' }}
        style={{ color: '#ef4444', width: '32px', height: '32px' }}
        onClick={() => {
          setDeletingNoticeId(rowData._id);
          setDeletingNotice(rowData);
          setDeleteModalOpen(true);
        }}
      />
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <div style={{ minHeight: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        <style>{`
          .p-datatable .p-datatable-thead > tr > th {
            background: #f9fafb !important; color: #6b7280 !important;
            font-size: 11px !important; font-weight: 700 !important;
            text-transform: uppercase !important; letter-spacing: 0.8px !important;
            padding: 16px 12px !important; border-bottom: 1px solid #e5e7eb !important;
          }
          .p-datatable .p-datatable-tbody > tr > td {
            padding: 16px 12px !important; border-bottom: 1px solid #f3f4f6 !important;
          }
          .p-datatable .p-datatable-tbody > tr:hover {
            background: linear-gradient(to right, rgba(65,105,225,0.04) 0%, rgba(65,105,225,0.02) 100%) !important;
            box-shadow: inset 4px 0 0 0 #4169E1;
          }
        `}</style>

        <GlobalHeaderTitle
          title="Noticeboard"
          subtitle={`${paginationData.total} total notices`}
          icon="pi pi-bell"
          buttons={[{ label: 'Add Notice', icon: 'pi pi-plus', onClick: handleAddNotice }]}
        />

        <div style={{
          background: 'white', borderRadius: '20px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          border: '1px solid #e5e7eb', overflow: 'hidden', width: '100%'
        }}>
          {loading ? (
            <NoticeboardSkeletonLoader />
          ) : (
            <>
              {notices.length > 0 && (
                <div style={{
                  padding: '16px 24px', borderBottom: '1px solid #f3f4f6',
                  background: '#fafbfc', display: 'flex',
                  justifyContent: 'space-between', alignItems: 'center',
                  flexWrap: 'wrap', gap: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '32px', height: '32px', borderRadius: '8px',
                      background: 'linear-gradient(135deg, rgba(65,105,225,0.1) 0%, rgba(91,124,230,0.08) 100%)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <i className="pi pi-list" style={{ fontSize: '14px', color: '#4169E1' }} />
                    </div>
                    <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>
                      Showing {((page - 1) * paginationData.limit) + 1} – {Math.min(page * paginationData.limit, paginationData.total)} of {paginationData.total} notices
                    </span>
                  </div>
                  <div style={{
                    padding: '8px 16px', borderRadius: '12px',
                    background: 'linear-gradient(135deg, #4169E1 0%, #5B7CE6 100%)',
                    boxShadow: '0 4px 12px rgba(65,105,225,0.3)'
                  }}>
                    <span style={{ fontSize: '13px', color: 'white', fontWeight: 700 }}>
                      Page {paginationData.page} of {paginationData.total_pages}
                    </span>
                  </div>
                </div>
              )}

              <DataTable
                value={notices} lazy rows={paginationData.limit}
                first={(page - 1) * paginationData.limit}
                totalRecords={paginationData.total} paginator={false}
                emptyMessage={
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', minHeight: '400px' }}>
                    <div style={{
                      width: '56px', height: '56px',
                      background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)',
                      borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginBottom: '20px', boxShadow: '0 6px 18px rgba(59,130,246,0.2)'
                    }}>
                      <i className="pi pi-bell" style={{ fontSize: '25px', color: 'white' }} />
                    </div>
                    <h3 style={{ margin: 0, color: '#1e40af', fontSize: '20px', fontWeight: 700, marginBottom: '8px' }}>No Notices Found</h3>
                    <p style={{ margin: 0, color: '#64748b', fontSize: '14px', fontWeight: 500, maxWidth: '400px', lineHeight: 1.6 }}>
                      There are no notices available at the moment. Create your first notice to get started!
                    </p>
                  </div>
                }
              >
                <Column header="#"                body={serialNumberTemplate} />
                <Column header="Title & Description" body={titleTemplate}        />
                <Column header="Priority"         body={priorityTemplate}     />
                <Column header="Posted By"        body={postedByTemplate}     />
                <Column header="Date & Time"      body={dateTimeTemplate}     />
                <Column header="Actions"          body={actionTemplate}       />
              </DataTable>

              {paginationData.total_pages > 1 && (
                <CustomPaginator
                  page={paginationData.page}
                  totalPages={paginationData.total_pages}
                  totalRecords={paginationData.total}
                  pageSize={paginationData.limit}
                  onPageChange={(newPage) => setPage(newPage)}
                  showInfoBar={false}
                />
              )}
            </>
          )}
        </div>
      </div>

      <ViewNoticeModal
        open={viewModalOpen}
        onClose={() => { setViewModalOpen(false); setSelectedNoticeId(null); }}
        noticeId={selectedNoticeId}
      />

      <AddNoticeModal
        open={addEditModalOpen}
        onClose={() => { setAddEditModalOpen(false); setEditingNoticeId(null); }}
        noticeId={editingNoticeId}
        onSuccess={handleSuccess}
      />

      <GlobalDeleteModal
        open={deleteModalOpen}
        onClose={() => { setDeleteModalOpen(false); setDeletingNoticeId(null); setDeletingNotice(null); }}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
        title="Delete Notice"
        subtitle="This will permanently delete the notice and all its attachments."
      >
        {deletingNotice && (
          <div style={{
            padding: '14px 16px', borderRadius: '10px',
            background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.15)'
          }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#1f2937', marginBottom: '4px' }}>
              {deletingNotice.title}
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
              {deletingNotice.body}
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span style={{
                fontSize: '11px', fontWeight: 600, color: '#ef4444',
                background: 'rgba(239,68,68,0.1)', padding: '2px 8px', borderRadius: '6px'
              }}>
                {deletingNotice.priority}
              </span>
              <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                by {deletingNotice.posted_by_name || 'Unknown'}
              </span>
            </div>
          </div>
        )}
      </GlobalDeleteModal>
    </>
  );
};

export default Noticeboard;